import React, { useState, useEffect, useMemo } from 'react';
import ContractList from './ContractList';
import ContractForm from './ContractForm';
import { parsePeriodValue, formatPeriod } from '../../utils/periodUtils';
import { 
  useApvContracts, 
  useCreateApvContract, 
  useUpdateApvContract, 
  useDeleteApvContract 
} from '../../hooks/useApvQuery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import './apv.css';

// Fallback seed data in case backend is offline
const INITIAL_MOCK_RECORDS = [
  {
    id: 'apv-2024-001',
    discipline: 'M&E (Mechanical & Electrical)',
    referenceNo: 'APV-2024-001',
    startDate: '2024-01-15',
    endDate: '2026-12-31',
    duration: '35 month, 2 week',
    durationYears: 0,
    durationMonths: 35,
    durationWeeks: 2,
    durationTotalMonths: 35,
    durationTotalWeeks: 142,
    basePeriod: '24 month',
    basePeriodYears: 0,
    basePeriodMonths: 24,
    basePeriodWeeks: 0,
    basePeriodTotalMonths: 24,
    basePeriodTotalWeeks: 96,
    baseAmount: 150000.00,
    optionPeriod: '11 month, 2 week',
    optionPeriodYears: 0,
    optionPeriodMonths: 11,
    optionPeriodWeeks: 2,
    optionPeriodTotalMonths: 11,
    optionPeriodTotalWeeks: 46,
    contingencyPercentage: 5,
    contingencyAmount: 11250.00,
    totalApv: 236250.00,
    vendors: ['TechCorp Engineering', 'HVAC Solutions Ltd'],
    remarks: 'Initial mechanical and electrical services contract'
  },
  {
    id: 'apv-2024-002',
    discipline: 'Architecture',
    referenceNo: 'APV-2024-002',
    startDate: '2024-03-01',
    endDate: '2025-08-31',
    duration: '18 month',
    durationYears: 0,
    durationMonths: 18,
    durationWeeks: 0,
    durationTotalMonths: 18,
    durationTotalWeeks: 72,
    basePeriod: '12 month',
    basePeriodYears: 0,
    basePeriodMonths: 12,
    basePeriodWeeks: 0,
    basePeriodTotalMonths: 12,
    basePeriodTotalWeeks: 48,
    baseAmount: 200000.00,
    optionPeriod: '6 month',
    optionPeriodYears: 0,
    optionPeriodMonths: 6,
    optionPeriodWeeks: 0,
    optionPeriodTotalMonths: 6,
    optionPeriodTotalWeeks: 24,
    contingencyPercentage: 5,
    contingencyAmount: 12738.10,
    totalApv: 267500.00,
    vendors: ['Design Studio Alpha', 'Urban Planning Co', 'Green Architecture Ltd'],
    remarks: 'Architectural consultancy and master plan details'
  },
  {
    id: 'apv-2024-003',
    discipline: 'Contractor',
    referenceNo: 'APV-2024-003',
    startDate: '2024-06-01',
    endDate: '2027-05-31',
    duration: '36 month',
    durationYears: 0,
    durationMonths: 36,
    durationWeeks: 0,
    durationTotalMonths: 36,
    durationTotalWeeks: 144,
    basePeriod: '30 month',
    basePeriodYears: 0,
    basePeriodMonths: 30,
    basePeriodWeeks: 0,
    basePeriodTotalMonths: 30,
    basePeriodTotalWeeks: 120,
    baseAmount: 500000.00,
    optionPeriod: '6 month',
    optionPeriodYears: 0,
    optionPeriodMonths: 6,
    optionPeriodWeeks: 0,
    optionPeriodTotalMonths: 6,
    optionPeriodTotalWeeks: 24,
    contingencyPercentage: 5,
    contingencyAmount: 32500.00,
    totalApv: 682500.00,
    vendors: ['BuildMax Construction', 'Premier Contractors Inc'],
    remarks: 'Main contracting works for building frame'
  }
];

function ApvManagement() {
  const [localContracts, setLocalContracts] = useState([]);
  const [activeSubMenu, setActiveSubMenu] = useState('records'); // 'records' or 'form'
  const [editingContract, setEditingContract] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // Track pending delete
  const [notification, setNotification] = useState(null); // { message, title, type }
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const showNotification = (message, title = 'Success', type = 'success') => {
    setNotification({ message, title, type });
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  // Safe project details retrieval
  const [projectName, setProjectName] = useState('NUS-ITT CPMS Project');

  // React Query Hooks
  const { data: apiContracts, isLoading, isError, refetch, setData: setApiContracts } = useApvContracts();
  const createMutation = useCreateApvContract();
  const updateMutation = useUpdateApvContract();
  const deleteMutation = useDeleteApvContract();

  // Load project details & local cache
  useEffect(() => {
    const savedDetails = localStorage.getItem('projectDetails');
    if (savedDetails) {
      try {
        const details = JSON.parse(savedDetails);
        if (details.projectName) {
          setProjectName(details.projectName);
        }
      } catch (e) {
        console.error('Error parsing projectDetails', e);
      }
    }

    const savedContracts = localStorage.getItem('apv_contracts');
    if (savedContracts) {
      try {
        const parsed = JSON.parse(savedContracts);
        if (Array.isArray(parsed)) {
          const normalized = parsed.map(c => {
            const pDur = parsePeriodValue(c.duration || { years: c.durationYears, months: c.durationMonths, weeks: c.durationWeeks });
            const pBase = parsePeriodValue(c.basePeriod || { years: c.basePeriodYears, months: c.basePeriodMonths, weeks: c.basePeriodWeeks });
            const pOpt = parsePeriodValue(c.optionPeriod || { years: c.optionPeriodYears, months: c.optionPeriodMonths, weeks: c.optionPeriodWeeks });
            return {
              ...c,
              duration: formatPeriod(0, pDur.totalMonths, pDur.weeks),
              durationYears: 0,
              durationMonths: pDur.totalMonths,
              durationWeeks: pDur.weeks,
              durationTotalMonths: pDur.totalMonths,
              durationTotalWeeks: pDur.totalWeeks,
              basePeriod: formatPeriod(0, pBase.totalMonths, pBase.weeks),
              basePeriodYears: 0,
              basePeriodMonths: pBase.totalMonths,
              basePeriodWeeks: pBase.weeks,
              basePeriodTotalMonths: pBase.totalMonths,
              basePeriodTotalWeeks: pBase.totalWeeks,
              optionPeriod: formatPeriod(0, pOpt.totalMonths, pOpt.weeks),
              optionPeriodYears: 0,
              optionPeriodMonths: pOpt.totalMonths,
              optionPeriodWeeks: pOpt.weeks,
              optionPeriodTotalMonths: pOpt.totalMonths,
              optionPeriodTotalWeeks: pOpt.totalWeeks,
            };
          });
          setLocalContracts(normalized);
        } else {
          setLocalContracts(INITIAL_MOCK_RECORDS);
        }
      } catch (e) {
        setLocalContracts(INITIAL_MOCK_RECORDS);
      }
    } else {
      setLocalContracts(INITIAL_MOCK_RECORDS);
    }
  }, []);

  // Compute active contracts: use backend data when available, fallback to local
  const contracts = useMemo(() => {
    if (apiContracts && Array.isArray(apiContracts)) {
      const normalized = apiContracts.map(c => {
        const pDur = parsePeriodValue(c.duration);
        const pBase = parsePeriodValue(c.basePeriod || c.base_period);
        const pOpt = parsePeriodValue(c.optionPeriod || c.option_period);
        return {
          ...c,
          duration: formatPeriod(0, pDur.totalMonths, pDur.weeks),
          durationYears: 0,
          durationMonths: pDur.totalMonths,
          durationWeeks: pDur.weeks,
          durationTotalMonths: pDur.totalMonths,
          durationTotalWeeks: pDur.totalWeeks,
          basePeriod: formatPeriod(0, pBase.totalMonths, pBase.weeks),
          basePeriodYears: 0,
          basePeriodMonths: pBase.totalMonths,
          basePeriodWeeks: pBase.weeks,
          basePeriodTotalMonths: pBase.totalMonths,
          basePeriodTotalWeeks: pBase.totalWeeks,
          optionPeriod: formatPeriod(0, pOpt.totalMonths, pOpt.weeks),
          optionPeriodYears: 0,
          optionPeriodMonths: pOpt.totalMonths,
          optionPeriodWeeks: pOpt.weeks,
          optionPeriodTotalMonths: pOpt.totalMonths,
          optionPeriodTotalWeeks: pOpt.totalWeeks,
        };
      });
      // Synchronize local cache
      localStorage.setItem('apv_contracts', JSON.stringify(normalized));
      return normalized;
    }
    return localContracts;
  }, [apiContracts, localContracts]);

  // Manual refresh handler
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      showNotification('Contract list refreshed!', 'Refreshed');
    } catch (err) {
      console.error('Manual refresh error:', err);
      showNotification('Failed to refresh data from server.', 'Error', 'danger');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Delete a contract — show custom confirmation dialog
  const handleDeleteContract = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    const targetId = deleteConfirmId;
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(targetId);
      await refetch();
      showNotification('Contract deleted successfully!', 'Success');
    } catch (err) {
      console.error('API delete error, deleting locally:', err);
      const updated = contracts.filter((c) => c.id !== targetId);
      setLocalContracts(updated);
      if (setApiContracts) setApiContracts(updated);
      localStorage.setItem('apv_contracts', JSON.stringify(updated));
      showNotification('Contract deleted successfully!', 'Success');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  // Switch to form for editing
  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setActiveSubMenu('form');
  };

  // Add contract (e.g. from CSV import)
  const handleAddContractDirectly = async (newContract) => {
    try {
      await createMutation.mutateAsync(newContract);
      await refetch();
      showNotification('Contract added successfully!', 'Success');
    } catch (err) {
      console.error('API create error, importing locally:', err);
      setLocalContracts(prev => {
        const filtered = prev.filter(c => c.referenceNo.toLowerCase() !== newContract.referenceNo.toLowerCase());
        const updated = [newContract, ...filtered];
        if (setApiContracts) setApiContracts(updated);
        localStorage.setItem('apv_contracts', JSON.stringify(updated));
        return updated;
      });
      showNotification('Contract added successfully!', 'Success');
    }
  };

  // Save contract (handles adding new and editing existing)
  const handleSaveContract = async (savedItem) => {
    const exists = contracts.some((c) => c.id === savedItem.id);
    setIsSaving(true);
    
    try {
      if (exists) {
        await updateMutation.mutateAsync({ id: savedItem.id, ...savedItem });
      } else {
        await createMutation.mutateAsync(savedItem);
      }
      // Re-fetch to immediately refresh the list with server data
      await refetch();
      setActiveSubMenu('records');
      setEditingContract(null);
      showNotification(
        exists ? 'Contract updated successfully!' : 'Contract created successfully!', 
        'Success'
      );
    } catch (err) {
      console.error('API save error:', err);
      // If validation error from backend (400), show error notification and do not save
      if (err.response && err.response.status === 400) {
        const errorMsg = err.response.data?.message || 'Validation error: Please check required fields.';
        showNotification(errorMsg, 'Validation Error', 'danger');
        setIsSaving(false);
        return;
      }
      let updated;
      if (exists) {
        updated = contracts.map((c) => (c.id === savedItem.id ? savedItem : c));
      } else {
        updated = [savedItem, ...contracts];
      }
      setLocalContracts(updated);
      if (setApiContracts) setApiContracts(updated);
      localStorage.setItem('apv_contracts', JSON.stringify(updated));

      setActiveSubMenu('records');
      setEditingContract(null);
      showNotification(
        exists ? 'Contract updated successfully!' : 'Contract created successfully!', 
        'Success'
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Switch tabs
  const handleTabChange = (tabName) => {
    setActiveSubMenu(tabName);
    if (tabName === 'records') {
      setEditingContract(null);
    }
  };

  return (
    <div className="wbshor">
      {/* Toast Alert Notification */}
      {notification && (
        <div className="apv-toast-container">
          <div className={`apv-alert-notif ${notification.type}`} role="alert">
            <div className="apv-alert-icon">
              <FontAwesomeIcon icon={notification.type === 'danger' ? faExclamationCircle : faCheckCircle} />
            </div>
            <div className="apv-alert-content">
              <div className="apv-alert-title">{notification.title}</div>
              <div className="apv-alert-message">{notification.message}</div>
            </div>
            <button 
              type="button" 
              className="apv-alert-close" 
              onClick={() => setNotification(null)}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Top Page Header */}
      <div className="login-header-wrap">
        <div className="login-header">
          <div className="pagetitle">{projectName}</div>
        </div>
      </div>

      {/* Main Subpage Inner Wrap */}
      <div className="container" style={{ height: 'calc(100vh - 120px)', padding: 0 }}>
        <div className="innerwrap" style={{ height: '100%', display: 'flex', width: '100%', overflow: 'hidden' }}>

          {/* Subpage Active View Content */}
          <div className="content" style={{ flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: activeSubMenu === 'records' ? 'auto' : 'hidden' }}>
            {activeSubMenu === 'records' ? (
              <ContractList 
                contracts={contracts} 
                onDelete={handleDeleteContract} 
                onEdit={handleEditContract}
                onAddClick={handleAddContractDirectly}
                onRefresh={handleManualRefresh}
                isRefreshing={isRefreshing}
              />
            ) : (
              <ContractForm 
                initialData={editingContract} 
                onSave={handleSaveContract} 
                onCancel={() => handleTabChange('records')}
                isSaving={isSaving}
              />
            )}
          </div>

        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="apv-dialog-overlay" onClick={cancelDelete}>
          <div className="apv-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="apv-dialog-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h4 className="apv-dialog-title">Delete Confirmation</h4>
            <p className="apv-dialog-message">Are you sure you want to delete this data?</p>
            <div className="apv-dialog-actions">
              <button className="apv-dialog-btn cancel" onClick={cancelDelete} disabled={isDeleting}>Cancel</button>
              <button className="apv-dialog-btn confirm" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApvManagement;
